import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import api from "../../../api/axiosConfig";
import { endpoints } from "../../../api/endpoints";

interface ActivityPeriodResponse {
  total?: number;
  percent_of_total?: number;
}

interface ScanActivityResponse {
  this_month?: ActivityPeriodResponse;
  this_week?: ActivityPeriodResponse;
  today?: ActivityPeriodResponse;
  total_scans?: number;
}

interface ComparisonCardItem {
  name: string;
  value: number;
  percentage: number;
  color: string;
}

interface ComparisonCardsProps {
  title: string;
  subtitle: string;
  data: ComparisonCardItem[];
  total: number;
  unit: string;
  isLoading?: boolean;
}

const numberFormatter = new Intl.NumberFormat("en-IN");

const scanActivityColors = {
  month: "#3B82F6",
  week: "#10B981",
  day: "#F59E0B",
};

const defaultScanActivityData: ComparisonCardItem[] = [
  { name: "Month", value: 0, percentage: 0, color: scanActivityColors.month },
  { name: "Week", value: 0, percentage: 0, color: scanActivityColors.week },
  { name: "Day", value: 0, percentage: 0, color: scanActivityColors.day },
];

const testComparisonData: ComparisonCardItem[] = [
  { name: "Month", value: 58, percentage: 40, color: "#3B82F6" },
  { name: "Week", value: 48, percentage: 33, color: "#10B981" },
  { name: "Day", value: 38, percentage: 26, color: "#F59E0B" },
];

const testComparisonTotal = testComparisonData.reduce(
  (sum, item) => sum + item.value,
  0,
);

const normalizePercentage = (value: unknown) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return 0;
  }

  return Math.max(0, Math.min(100, value));
};

const normalizeScanActivity = (payload: unknown) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {
      items: defaultScanActivityData,
      total: 0,
    };
  }

  const response = payload as ScanActivityResponse;

  return {
    items: [
      {
        name: "Month",
        value:
          typeof response.this_month?.total === "number"
            ? response.this_month.total
            : 0,
        percentage: normalizePercentage(response.this_month?.percent_of_total),
        color: scanActivityColors.month,
      },
      {
        name: "Week",
        value:
          typeof response.this_week?.total === "number"
            ? response.this_week.total
            : 0,
        percentage: normalizePercentage(response.this_week?.percent_of_total),
        color: scanActivityColors.week,
      },
      {
        name: "Day",
        value:
          typeof response.today?.total === "number" ? response.today.total : 0,
        percentage: normalizePercentage(response.today?.percent_of_total),
        color: scanActivityColors.day,
      },
    ],
    total: typeof response.total_scans === "number" ? response.total_scans : 0,
  };
};

const ComparisonCards: React.FC<ComparisonCardsProps> = ({
  title,
  subtitle,
  data,
  total,
  unit,
  isLoading = false,
}) => {
  return (
    <Paper sx={{ p: 2, borderRadius: 2, height: "100%" }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <Stack spacing={2}>
        {data.map((item) => {
          return (
            <Card
              key={item.name}
              sx={{
                bgcolor: `${item.color}08`,
                borderLeft: `4px solid ${item.color}`,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      {item.name}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: "bold", color: item.color, my: 1 }}
                    >
                      {numberFormatter.format(item.value)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {unit}
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={item.percentage}
                  sx={{
                    mt: 2,
                    height: 6,
                    borderRadius: 3,
                    bgcolor: "#E5E7EB",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: item.color,
                      borderRadius: 3,
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  {item.percentage.toFixed(1)}% of total
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <Box
        sx={{
          mt: 2,
          p: 2,
          bgcolor: "#F5F5F5",
          borderRadius: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="body2" color="textSecondary">
          Total {unit}: <strong>{numberFormatter.format(total)}</strong>
        </Typography>
      </Box>
    </Paper>
  );
};

export const ComparisonCardsChart: React.FC = () => {
  const [scanActivityData, setScanActivityData] = useState(
    defaultScanActivityData,
  );
  const [totalScans, setTotalScans] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchScanActivity = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await api.get(endpoints.dashboard.scanActivity, {
          signal: controller.signal,
        });

        const normalized = normalizeScanActivity(response.data);
        setScanActivityData(normalized.items);
        setTotalScans(normalized.total);
      } catch (err: any) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return;
        }

        const message =
          err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load scan activity.";

        setErrorMessage(message);
        setScanActivityData(defaultScanActivityData);
        setTotalScans(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchScanActivity();

    return () => {
      controller.abort();
    };
  }, []);

  return (
    <Box sx={{ p: 3, bgcolor: "#F5F5F5", minHeight: "100vh" }}>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ComparisonCards
            title="Scan Activity"
            subtitle="Daily scan count overview"
            data={scanActivityData}
            total={totalScans}
            unit="scans"
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ComparisonCards
            title="Testing Activity Chart"
            subtitle="Quality assurance and testing metrics"
            data={testComparisonData}
            total={testComparisonTotal}
            unit="tests"
          />
        </Grid>
      </Grid>
    </Box>
  );
};
